import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedDatePicker from '../ui/LocalizedDatePicker';

interface InvoiceMetaFormProps {
  invoiceDate: string;
  setInvoiceDate: (value: string) => void;
  currency: string;
  setCurrency: (value: string) => void;
  deliveryDate: string | null;
  setDeliveryDate: (value: string | null) => void;
  invoiceNumberPreview?: string | null;
  isLoadingPreview?: boolean;
  showTitle?: boolean;
}

const InvoiceMetaForm: React.FC<InvoiceMetaFormProps> = ({
  invoiceDate,
  setInvoiceDate,
  currency,
  setCurrency,
  deliveryDate,
  setDeliveryDate,
  invoiceNumberPreview,
  isLoadingPreview = false,
  showTitle = true,
}) => {
  const { t } = useTranslation('invoice');
  return (
    <div className={showTitle ? "p-4 border border-hairline rounded-md shadow-sm bg-surface-raised mb-6" : ""}>
      {showTitle && <h2 className="text-xl font-semibold mb-4 text-primary">{t('details.data')}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Rechnungsnummer-Vorschau */}
        <div>
          <label className="block text-sm font-medium text-secondary mb-1">
            {t('nextNumber')}
          </label>
          <input
            type="text"
            value={isLoadingPreview ? t('loading', { ns: 'common' }) : (invoiceNumberPreview || 'Wird automatisch generiert')}
            readOnly
            className="mt-1 block w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded-md shadow-sm sm:text-sm text-secondary cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-muted">
            {t('numberPreview')}
          </p>
        </div>

        {/* Rechnungsdatum */}
        <div>
          <label htmlFor="invoiceDate" className="block text-sm font-medium text-secondary mb-1">
            {t('invoiceDate')} <span className="text-red-500">*</span>
          </label>
          <LocalizedDatePicker
            id="invoiceDate"
            name="invoiceDate"
            value={invoiceDate}
            onChange={setInvoiceDate}
            required
            placeholder={t('invoiceDate')}
          />
        </div>

        {/* NEU: Lieferdatum */}
        <div>
          <label htmlFor="deliveryDate" className="block text-sm font-medium text-secondary mb-1">
            {t('deliveryDate')}
          </label>
          <LocalizedDatePicker
            id="deliveryDate"
            name="deliveryDate"
            value={deliveryDate || ''}
            onChange={(value) => setDeliveryDate(value || null)}
            placeholder={t('deliveryDate')}
          />
          <p className="mt-1 text-xs text-muted">
            {t('deliveryDateHint')}
          </p>
        </div>

        {/* Währung */}
        <div>
          <label htmlFor="currency" className="block text-sm font-medium text-secondary mb-1">
            {t('currency')} <span className="text-red-500">*</span>
          </label>
          <select
            id="currency"
            name="currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            required
            className="select-field"
          >
            <option value="EUR">{t('currencyEUR')}</option>
            <option value="USD">USD - US-Dollar</option>
            <option value="CHF">CHF - Schweizer Franken</option>
            <option value="GBP">GBP - Britisches Pfund</option>
            <option value="CNY">CNY - Chinesischer Yuan</option>
            <option value="JPY">JPY - Japanischer Yen</option>
            <option value="CAD">CAD - Kanadischer Dollar</option>
            <option value="AUD">AUD - Australischer Dollar</option>
            <option value="PLN">PLN - Polnischer Zloty</option>
            <option value="CZK">CZK - Tschechische Krone</option>
            <option value="DKK">DKK - Dänische Krone</option>
            <option value="SEK">SEK - Schwedische Krone</option>
            <option value="NOK">NOK - Norwegische Krone</option>
            {currency && !["EUR", "USD", "CHF", "GBP", "CNY", "JPY", "CAD", "AUD", "PLN", "CZK", "DKK", "SEK", "NOK"].includes(currency) && (
              <option value={currency}>{currency} - Benutzerdefiniert</option>
            )}
          </select>
          <div className="mt-2 flex">
            <input
              type="text"
              placeholder="Andere Währung (ISO-3 Code)"
              value={currency}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                if (/^[A-Z]{0,3}$/.test(value)) {
                  setCurrency(value);
                }
              }}
              className="text-xs px-2 py-1 border border-strong bg-surface-inset text-primary rounded-l focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 w-full max-w-32"
              maxLength={3}
            />
            <button
              type="button"
              onClick={() => {
                if (currency.length === 3) {
                  // Akzeptiere die benutzerdefinierte Währung
                } else {
                  setCurrency('EUR');
                }
              }}
              className="text-xs px-2 py-1 bg-indigo-600 text-[#0C1322] rounded-r hover:bg-indigo-700 transition"
            >
              {t('apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceMetaForm; 